import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";

import { DataTable } from "@/components/data";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";

import { fractionPct } from "./discounts";
import { Failed, PaneSkeleton } from "./DiscountParts";
import { ABSENT, humanDate, messageFor, money, personName, stamp } from "./format";
import { downloadCsv } from "./logs";
import {
  applyRunText, canCancel, canExcept, canExport, canRetry, cancelChange, cancelRefusal, CHANGE_SHOWS, CHANGES_KEY, changeEvents, changeQuery, changeRefusal,
  changeShowCount, changesQuery, confirmNoticeSent, DRIFT_KEY, exceptionOf, exceptionRefusal, exportChange, lastStep, logException, MAX_REASON, missingSteps,
  NOTICE_STEPS, noticeActions, noticeRefusal, priceText, productName, reachText, recordStep, resultText, retried, retryRefusal, retryTarget, searchForChange,
  searchForChangeShow, subscriberParams, subscriptionNumber, SUBSCRIBERS_COPY, SUBSCRIBERS_PAGE_SIZE, whenText,
  type ChangeEvent, type ChangeRow, type ChangeTarget, type NoticeStep, type SubscriberMarket, type TargetState,
} from "./subscribers";

const T = SUBSCRIBERS_COPY;
const C = T.changes;
const D = C.detail;
const K = D.cancel;
const N = D.notice;
const E = C.exceptionSheet;
const X = D.exception;
const RT = D.retry;
const RS = D.results;
type Note = { tone: "ok" | "err"; text: string };
/** One exception as the form sends it: the rise, the subscription and the reason. */
type ExceptionDraft = { changeId: number; subscriptionId: number; reason: string };
const CHIP_TONE: Record<ChangeRow["direction"], string> = { up: "border-ks-warn text-ks-warn", down: "border-ks-pass text-ks-pass" };
const STATE_TONE: Record<ChangeRow["state"], string> = {
  planned: "text-ks-ink", notice_staged: "border-ks-purple text-ks-purple", notice_sent: "border-ks-purple text-ks-purple", applying: "border-ks-purple text-ks-purple",
  applied: "border-ks-pass text-ks-pass", cancelled: "border-ks-danger text-ks-danger",
};
const RESULT_TONE: Record<TargetState, string> = {
  pending: "text-ks-ink", excepted: "border-ks-purple text-ks-purple", written: "border-ks-purple text-ks-purple", applied: "border-ks-pass text-ks-pass",
  failed: "border-ks-danger text-ks-danger", skipped: "border-ks-warn text-ks-warn",
};

/** The Price changes pane (C1 v1.44, c1-subscription-changes): C2 v1.45's changes of the store, the ones that need a person first
 * (C2's order), each with who it reaches, its state and what moves it next; one change open in the pane (`pp_change`) with its
 * history and its affected subscriptions paged; and cancel with a reason. C1 v1.45 (c1-subscription-notice): a rise's notice
 * card with its steps, Confirm notice sent and the subscribers' download (C2 v1.46). C1 v1.46 (c1-subscription-exceptions): Log
 * exception, from the list and from a sent rise, keeps one subscriber on the old price (C2 v1.47); a target reads its exception; a
 * drift correction reads as one. C1 v1.47 (c1-subscription-results): the Applying view, the applier's last run on the list and on a
 * change, each subscription's apply result in words, and Retry on a failed one (C2 v1.48). The browser works nothing out: every
 * count, price, date, state and result is C2's. */
export function SubscriberChangesPane({ areaName }: { areaName: string }) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const go = (next: Record<string, unknown>) => void navigate({ to: ".", search: next, replace: true });
  const params = subscriberParams(search);
  if (params.change !== null) {
    return <ChangeView key={`${params.market}|${params.change}`} id={params.change} market={params.market} region={T.regionLabel[params.region]} areaName={areaName}
      page={params.page} onBack={() => go(searchForChange(search, null))} onPageChange={(next) => go({ ...search, pp_spage: next || undefined })} />;
  }
  return <ChangesList market={params.market} region={T.regionLabel[params.region]} show={params.cshow} page={params.page} areaName={areaName}
    onShow={(show) => go(searchForChangeShow(search, show))} onOpen={(id) => go(searchForChange(search, id))} onPageChange={(next) => go({ ...search, pp_spage: next || undefined })} />;
}

function ChangesList({ market, region, show, page: pageIndex, areaName, onShow, onOpen, onPageChange }: {
  market: SubscriberMarket; region: string; show: ChangeRow["state"] | "open" | "all"; page: number; areaName: string;
  onShow: (show: ChangeRow["state"] | "open" | "all") => void; onOpen: (id: number) => void; onPageChange: (next: number) => void;
}) {
  const page = useQuery(changesQuery(market, show, pageIndex));
  // the summary and the read are the same in every answer of a store: while a view's or a page's own read is on its way they come
  // from the store's default read, so the view buttons stay on screen and only the table waits (the Drift pane's rule)
  const base = useQuery(changesQuery(market, "open", 0));
  const [note, setNote] = useState<Note | null>(null);
  const [excepting, setExcepting] = useState(false);
  const heading = <div><h3 className="text-base font-semibold text-ks-ink">{C.title}</h3><p className="text-sm text-ks-grey-1">{C.note}</p></div>;
  const failed = page.error ? <Failed sentence={`${C.error} ${messageFor(page.error, areaName)}`} onRetry={() => void page.refetch()} /> : null;
  const d = page.data ?? base.data;
  if (!d) return <div className="flex flex-col gap-3">{heading}{failed ?? <PaneSkeleton />}</div>;
  const notices = (
    <>
      {d.read?.stale && <Alert role="status"><AlertDescription>{C.stale(d.read)}</AlertDescription></Alert>}
      {d.read === null && <Alert role="status"><AlertDescription>{C.neverRead(region)}</AlertDescription></Alert>}
    </>
  );
  if (d.summary.changes === 0) {
    return <div className="flex flex-col gap-3">{heading}{notices}<Card><CardContent className="py-10 text-center"><p className="text-lg font-semibold text-ks-ink">{C.emptyTitle}</p><p className="text-ks-grey-1">{C.emptyBody}</p></CardContent></Card></div>;
  }
  const columns: ColumnDef<ChangeRow, unknown>[] = [
    { id: "product", header: C.columns[0], cell: ({ row: r }) => <ProductCell row={r.original} region={region} onOpen={() => onOpen(r.original.id)} /> },
    { id: "change", header: C.columns[1], cell: ({ row: r }) => <ChangeCell row={r.original} /> },
    { id: "subscribers", header: C.columns[2], cell: ({ row: r }) => <span className="text-sm tabular-nums text-ks-ink">{reachText(r.original)}</span> },
    { id: "state", header: C.columns[3], cell: ({ row: r }) => <StateCell row={r.original} /> },
    { id: "when", header: C.columns[4], cell: ({ row: r }) => <span className="text-sm text-ks-grey-1">{whenText(r.original)}</span> },
  ];
  return (
    <div className="flex flex-col gap-3">
      {heading}
      {notices}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div role="group" aria-label={C.showLabel} className="flex flex-wrap gap-1">
          {CHANGE_SHOWS.map((key) => (
            <Button key={key} size="sm" variant={key === show ? "default" : "outline"} aria-pressed={key === show} data-nonmutating onClick={() => onShow(key)}>
              {C.show[key]} ({changeShowCount(d.summary, key)})
            </Button>
          ))}
        </div>
        {/* an exception is logged on a rise whose notice is sent: offered only when the store has one */}
        {d.summary.notice_sent > 0 && <Button size="sm" variant="outline" onClick={() => { setNote(null); setExcepting(true); }} data-exception-open>{E.button}</Button>}
      </div>
      {note && !excepting && <NoteLine note={note} />}
      {!page.data
        ? failed ?? <PaneSkeleton />
        : <DataTable columns={columns} rows={d.rows} page={{ pageIndex, pageSize: SUBSCRIBERS_PAGE_SIZE, rowCount: d.page.total }} onPageChange={onPageChange}
            onRowClick={(row) => onOpen(row.id)} caption={C.title} emptyMessage={C.noMatch} density="compact" />}
      <p className="text-xs text-ks-grey-1"><span data-apply-run>{applyRunText(d.apply_run)}</span> {d.read ? `${T.read(d.read)}. ` : ""}{C.footnote}</p>
      <ExceptionSheet market={market} change={null} open={excepting} areaName={areaName} onClose={() => setExcepting(false)}
        onDone={(text) => { setExcepting(false); setNote({ tone: "ok", text }); }} />
    </div>
  );
}

/** One change: its header, the cancel, a rise's notice card (C1 v1.45), its history and its affected subscriptions paged. The status
 * or alert of a write is this view's and lives as long as the change is open; one write runs at a time. */
function ChangeView({ id, market, region, areaName, page: pageIndex, onBack, onPageChange }: {
  id: number; market: SubscriberMarket; region: string; areaName: string; page: number; onBack: () => void; onPageChange: (next: number) => void;
}) {
  const client = useQueryClient();
  const [note, setNote] = useState<Note | null>(null);
  const [open, setOpen] = useState(false);
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [excepting, setExcepting] = useState(false);
  const [retrying, setRetrying] = useState<ChangeTarget | null>(null);
  const page = useQuery(changeQuery(market, id, pageIndex));
  const back = <Button size="sm" variant="outline" data-nonmutating onClick={onBack} data-change-back>← {D.back}</Button>;
  const cancel = useMutation({
    mutationFn: (reason: string) => cancelChange(market, id, reason),
    onSuccess: (answer) => {
      // the answer is the change as the detail reads it (its targets on the first page): shown at once, then every read of every
      // change and list is read again, the open and cancelled counts among them
      client.setQueryData(changeQuery(market, id, 0).queryKey, answer);
      setOpen(false);
      setNote({ tone: "ok", text: K.done });
      if (pageIndex !== 0) onPageChange(0);   // the answer is the first page of targets: show the change afresh from it (round 1, R1-5)
    },
    onError: (error) => setNote({ tone: "err", text: cancelRefusal(error, areaName) }),
    onSettled: () => { void client.invalidateQueries({ queryKey: CHANGES_KEY }); },
  });
  // a notice step or Confirm notice sent: the answer is the change as the detail reads it, shown at once as the cancel's is
  const notice = useMutation({
    mutationFn: (action: { kind: NoticeStep | "notice_sent"; note: string }) =>
      (action.kind === "notice_sent" ? confirmNoticeSent(market, id, action.note) : recordStep(market, id, action.kind)),
    onSuccess: (answer, action) => {
      client.setQueryData(changeQuery(market, id, 0).queryKey, answer);
      setNoticeOpen(false);
      setNote({ tone: "ok", text: action.kind === "notice_sent" ? N.dialog.done(answer.change.effective_date) : N.recorded(N.step[action.kind]) });
      if (pageIndex !== 0) onPageChange(0);
    },
    onError: (error) => setNote({ tone: "err", text: noticeRefusal(error, areaName) }),
    onSettled: () => { void client.invalidateQueries({ queryKey: CHANGES_KEY }); },
  });
  // the subscribers' download: C2's file handed to the browser; C2 records the download, so the history is read again
  const download = useMutation({
    mutationFn: () => exportChange(market, id),
    onSuccess: ({ text, name }) => { downloadCsv(text, name ?? N.file(id)); setNote({ tone: "ok", text: N.downloaded }); },
    onError: (error) => setNote({ tone: "err", text: noticeRefusal(error, areaName) }),
    onSettled: () => { void client.invalidateQueries({ queryKey: CHANGES_KEY }); },
  });
  // a retry (C1 v1.47): C2 answers the change as the detail reads it, the retried subscription first among the targets; shown at
  // once, then every changes read is read again (the Applying count moves when an applied change reopens)
  const retry = useMutation({
    mutationFn: (target: ChangeTarget) => retryTarget(market, id, target.id),
    onSuccess: (answer, target) => {
      client.setQueryData(changeQuery(market, id, 0).queryKey, answer);
      setRetrying(null);
      setNote({ tone: "ok", text: RT.done(target.subscription_id) });
      if (pageIndex !== 0) onPageChange(0);
    },
    onError: (error) => setNote({ tone: "err", text: retryRefusal(error, areaName) }),
    onSettled: () => { void client.invalidateQueries({ queryKey: CHANGES_KEY }); },
  });
  const busy = cancel.isPending || notice.isPending || download.isPending || retry.isPending;
  const close = () => { setOpen(false); setNote(null); };
  const closeRetry = () => { setRetrying(null); setNote(null); };
  const closeNotice = () => { setNoticeOpen(false); setNote(null); };
  if (page.isPending) return <div className="flex flex-col gap-3">{back}<PaneSkeleton /></div>;
  if (page.error) return <div className="flex flex-col gap-3">{back}<Failed sentence={`${C.detailError} ${changeRefusal(page.error, areaName)}`} onRetry={() => void page.refetch()} /></div>;
  const d = page.data;
  const c = d.change;
  const cancelled = c.state === "cancelled" ? [...d.events].reverse().find((e) => e.kind === "cancelled" && e.target_id === null) ?? null : null;
  const columns: ColumnDef<ChangeTarget, unknown>[] = [
    { id: "subscription", header: D.targetColumns[0], cell: ({ row: r }) => <TargetCell target={r.original} /> },
    { id: "old", header: D.targetColumns[1], meta: { align: "right" }, cell: ({ row: r }) => <span className="tabular-nums text-ks-ink">{money(r.original.old_sub_price, d.currency)}</span> },
    { id: "new", header: D.targetColumns[2], meta: { align: "right" }, cell: ({ row: r }) => <span className="tabular-nums text-ks-ink">{money(r.original.new_sub_price, d.currency)}</span> },
    { id: "discount", header: D.targetColumns[3], meta: { align: "right" }, cell: ({ row: r }) => <span className="tabular-nums text-ks-grey-1">{r.original.rate === null ? ABSENT : `${fractionPct(r.original.rate)}%`}</span> },
    { id: "result", header: D.targetColumns[4], cell: ({ row: r }) => (
      <ResultCell target={r.original} currency={d.currency} waiting={retried(d.events, r.original.id)} allowed={canRetry(c, r.original)} busy={busy}
        onRetry={() => { setNote(null); setRetrying(r.original); }} />
    ) },
  ];
  const hasResults = c.state === "applying" || c.state === "applied" || (c.state === "cancelled" && c.targets.applied + c.targets.failed > 0);
  return (
    <div className="flex flex-col gap-3" data-change-view={c.id}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        {back}
        {canCancel(c) && <Button size="sm" variant="outline" disabled={busy} onClick={() => { setNote(null); setOpen(true); }}>{K.button}</Button>}
      </div>
      {note && !open && !noticeOpen && !excepting && retrying === null && <NoteLine note={note} />}
      <Card>
        <CardContent className="flex flex-wrap items-start justify-between gap-4 py-4">
          <div className="flex flex-col gap-1">
            <h3 className="text-base font-semibold text-ks-ink">{productName(c)} <span className="font-normal text-ks-grey-1">· {c.xero_sku} · {region}</span></h3>
            <ChangeCell row={c} />
            <p className="text-xs text-ks-grey-1">{(c.source === "drift_correction" ? D.corrected : D.committed)(personName(c.created_by), stamp(c.created_at))}</p>
          </div>
          <div className="flex flex-col items-end gap-1 text-right">
            <StateChip state={c.state} />
            <p className="text-sm tabular-nums text-ks-ink">{reachText(c)}</p>
            <p className="text-xs text-ks-grey-1">{whenText(c)}</p>
          </div>
        </CardContent>
      </Card>
      {cancelled && <Alert role="status" data-change-cancelled><AlertDescription>{D.cancelledBy(personName(cancelled.actor), humanDate(cancelled.at), cancelled.note ?? "")}</AlertDescription></Alert>}
      {hasResults && (
        <Card data-change-results>
          <CardContent className="flex flex-col gap-1 py-4">
            <p className="text-sm font-semibold text-ks-ink">{RS.title}</p>
            <p className="text-sm tabular-nums text-ks-ink">{reachText(c)}</p>
            <p className="text-xs text-ks-grey-1" data-apply-run>{applyRunText(d.apply_run)}</p>
            {c.targets.failed > 0 && <p className="text-xs text-ks-danger" data-results-failed>{RS.failed(c.targets.failed)}</p>}
          </CardContent>
        </Card>
      )}
      {c.direction === "up" && (
        <NoticeCard change={c} events={d.events} busy={busy} downloading={download.isPending}
          onStep={(kind) => { setNote(null); notice.mutate({ kind, note: "" }); }}
          onConfirm={() => { setNote(null); setNoticeOpen(true); }} onDownload={() => { setNote(null); download.mutate(); }} />
      )}
      <div className="grid gap-3 md:grid-cols-[minmax(240px,1fr)_2fr]">
        <Card>
          <CardContent className="flex flex-col gap-2 py-4">
            <p className="text-sm font-semibold text-ks-ink">{D.history}</p>
            <ul className="flex flex-col gap-2" data-change-history>
              {changeEvents(d.events).map((e) => (
                <li key={e.id} className="text-sm">
                  <span className="text-ks-ink">{D.event[e.kind] ?? e.kind}</span> <span className="text-xs text-ks-grey-1">· {personName(e.actor)} · {stamp(e.at)}</span>
                  {e.note && <div className="text-xs text-ks-grey-1">{e.note}</div>}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-semibold text-ks-ink">{D.targetsTitle(d.page.total)}</p>
            {canExcept(c) && <Button size="sm" variant="outline" disabled={busy} onClick={() => { setNote(null); setExcepting(true); }} data-exception-open>{E.button}</Button>}
          </div>
          <DataTable columns={columns} rows={d.targets} page={{ pageIndex, pageSize: SUBSCRIBERS_PAGE_SIZE, rowCount: d.page.total }} onPageChange={onPageChange}
            caption={D.targetsTitle(d.page.total)} emptyMessage={D.noTargets} density="compact" />
        </div>
      </div>
      <p className="text-xs text-ks-grey-1">{d.read ? `${T.read(d.read)}. ` : ""}{C.footnote}</p>
      <Dialog open={open} onOpenChange={(next) => { if (next) setOpen(true); else if (!cancel.isPending) close(); }}>
        <DialogContent showCloseButton={false} className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{K.title}</DialogTitle>
            <DialogDescription>{K.body(c)}</DialogDescription>
          </DialogHeader>
          {open && note && <NoteLine note={note} />}
          {open && <CancelChangeForm pending={cancel.isPending} onKeep={close} onCancel={(reason) => cancel.mutate(reason)} onNote={setNote} />}
        </DialogContent>
      </Dialog>
      <Dialog open={noticeOpen} onOpenChange={(next) => { if (next) setNoticeOpen(true); else if (!notice.isPending) closeNotice(); }}>
        <DialogContent showCloseButton={false} className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{N.dialog.title}</DialogTitle>
            <DialogDescription>{N.dialog.body(c, missingSteps(d.events))}</DialogDescription>
          </DialogHeader>
          {noticeOpen && note && <NoteLine note={note} />}
          {noticeOpen && <NoticeSentForm pending={notice.isPending} onKeep={closeNotice} onConfirm={(text) => notice.mutate({ kind: "notice_sent", note: text })} />}
        </DialogContent>
      </Dialog>
      <ExceptionSheet market={market} change={c} open={excepting} areaName={areaName} onClose={() => setExcepting(false)}
        onDone={(text) => { setExcepting(false); setNote({ tone: "ok", text }); if (pageIndex !== 0) onPageChange(0); }} />
      <Dialog open={retrying !== null} onOpenChange={(next) => { if (next) return; if (!retry.isPending) closeRetry(); }}>
        <DialogContent showCloseButton={false} className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{RT.title}</DialogTitle>
            <DialogDescription>{retrying ? RT.body(retrying.subscription_id, retrying.new_sub_price === null ? ABSENT : money(retrying.new_sub_price, d.currency)) : ""}</DialogDescription>
          </DialogHeader>
          {retrying && note && <NoteLine note={note} />}
          {retrying && <RetryForm pending={retry.isPending} onKeep={closeRetry} onConfirm={() => retry.mutate(retrying)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** The Log exception sheet (v26's drawer, C1 v1.46): one subscriber of a rise whose notice is sent keeps the old price, as their
 * reply asked. Opened from a sent rise, the rise is that change; from the list, the person picks it among the store's sent rises
 * (C2's Notice sent view). C2 answers the change as the detail reads it, in the change's own market: it is shown at once, the
 * status is the opener's, and every changes read and every drift read is read again (the row leaves the attention view). A
 * refusal reads inside the sheet, where the person is; the sheet refuses to close while the write runs. */
function ExceptionSheet({ market, change, open, areaName, onClose, onDone }: {
  market: SubscriberMarket; change: ChangeRow | null; open: boolean; areaName: string; onClose: () => void; onDone: (status: string) => void;
}) {
  const client = useQueryClient();
  const [note, setNote] = useState<Note | null>(null);
  const sent = useQuery({ ...changesQuery(market, "notice_sent", 0), enabled: open && change === null });
  const save = useMutation({
    mutationFn: (draft: ExceptionDraft) => logException(draft.changeId, draft.subscriptionId, draft.reason),
    onSuccess: (answer, draft) => {
      client.setQueryData(changeQuery(answer.market, answer.change.id, 0).queryKey, answer);
      setNote(null);
      onDone(E.done(draft.subscriptionId, exceptionOf(answer, draft.subscriptionId)?.deliveries_remaining ?? null));
    },
    onError: (error) => setNote({ tone: "err", text: exceptionRefusal(error, areaName) }),
    onSettled: () => { void client.invalidateQueries({ queryKey: CHANGES_KEY }); void client.invalidateQueries({ queryKey: DRIFT_KEY }); },
  });
  const close = () => { if (!save.isPending) { setNote(null); onClose(); } };
  const rises = change ? [change] : (sent.data?.rows ?? []).filter(canExcept);
  // from the list: a read that fails says so in the sheet, and a store with no sent rise says there is nothing to log on
  const readFailed = change === null && sent.isError;
  const noneSent = change === null && sent.isSuccess && rises.length === 0;
  return (
    <Sheet open={open} onOpenChange={(next) => { if (!next) close(); }}>
      <SheetContent side="right" className="overflow-y-auto data-[side=right]:w-full data-[side=right]:sm:max-w-[480px]">
        <SheetHeader>
          <SheetTitle>{E.title}</SheetTitle>
          <SheetDescription>{E.intro}</SheetDescription>
        </SheetHeader>
        {open && note && <div className="px-4"><NoteLine note={note} /></div>}
        {open && !note && readFailed && <div className="px-4"><NoteLine note={{ tone: "err", text: exceptionRefusal(sent.error, areaName) }} /></div>}
        {open && <ExceptionForm rises={rises} fixed={change !== null} noneSent={noneSent} pending={save.isPending} onCancel={close} onNote={setNote} onSave={(draft) => save.mutate(draft)} />}
      </SheetContent>
    </Sheet>
  );
}

/** The exception form: the Recharge subscription number, the rise, and a required reason. The screen refuses a number that is not
 * one, no rise, an empty reason or one naming an email before C2 does (C2 refuses `@` in a reason: it is kept for everyone with the
 * module view). Its own component so the server-side harness proves it outside the sheet, as the cancel form is. */
export function ExceptionForm({ rises, fixed, noneSent = false, pending, onCancel, onNote, onSave }: {
  rises: ChangeRow[]; fixed: boolean; noneSent?: boolean; pending: boolean; onCancel: () => void; onNote: (note: Note | null) => void; onSave: (draft: ExceptionDraft) => void;
}) {
  const [typed, setTyped] = useState("");
  const [picked, setPicked] = useState(fixed && rises[0] ? String(rises[0].id) : "");
  const [reason, setReason] = useState("");
  const submit = () => {
    const subscriptionId = subscriptionNumber(typed);
    const rise = rises.find((r) => String(r.id) === picked);
    if (subscriptionId === null) { onNote({ tone: "err", text: E.noSubscription }); return; }
    if (!rise) { onNote({ tone: "err", text: E.noChange }); return; }
    if (!reason.trim()) { onNote({ tone: "err", text: E.empty }); return; }
    if (reason.includes("@")) { onNote({ tone: "err", text: E.email }); return; }
    onNote(null); onSave({ changeId: rise.id, subscriptionId, reason });
  };
  return (
    <div className="flex flex-col gap-3 px-4 pb-4" data-exception-form>
      <div className="flex flex-col gap-1">
        <Label htmlFor="pp-exception-subscription" className="text-sm">{E.subscription}</Label>
        <Input id="pp-exception-subscription" inputMode="numeric" autoComplete="off" value={typed} placeholder={E.subscriptionPlaceholder} onChange={(e) => setTyped(e.target.value)} />
      </div>
      <div className="flex flex-col gap-1">
        <Label className="text-sm">{E.change}</Label>
        {fixed && rises[0]
          ? <p className="text-sm text-ks-ink" data-exception-rise={rises[0].id}>{E.option(rises[0])}</p>
          : noneSent
          ? <p className="text-sm text-ks-grey-1" data-exception-none>{E.noneSent}</p>
          : (
            <Select value={picked} onValueChange={setPicked}>
              <SelectTrigger aria-label={E.change} data-exception-pick><SelectValue placeholder={E.pick} /></SelectTrigger>
              <SelectContent>{rises.map((r) => <SelectItem key={r.id} value={String(r.id)}>{E.option(r)}</SelectItem>)}</SelectContent>
            </Select>
          )}
      </div>
      <div className="flex flex-col gap-1">
        <Label htmlFor="pp-exception-reason" className="text-sm">{E.label}</Label>
        <Textarea id="pp-exception-reason" value={reason} maxLength={MAX_REASON} rows={3} placeholder={E.placeholder} onChange={(e) => setReason(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Escape") onCancel(); }} />
        <p className="text-xs text-ks-grey-1">{E.hint}</p>
      </div>
      <div className="flex justify-end gap-1">
        <Button size="sm" variant="outline" data-nonmutating disabled={pending} onClick={onCancel}>{E.cancel}</Button>
        <Button size="sm" disabled={pending || rises.length === 0} onClick={submit}>{pending ? E.pending : E.save}</Button>
      </div>
    </div>
  );
}

/** A rise's notice card (C1 v1.45): the four steps in order, each with who recorded it last and when, or "Not yet"; the actions C2
 * accepts now (`noticeActions`) and the subscribers' download (`canExport`). Every action is a write: none is marked
 * non-mutating, so a person with the view alone reads the card and cannot move it. The card is its own component so the
 * server-side harness proves it outside the view. */
export function NoticeCard({ change, events, busy, downloading, onStep, onConfirm, onDownload }: {
  change: ChangeRow; events: ChangeEvent[]; busy: boolean; downloading: boolean; onStep: (kind: NoticeStep) => void; onConfirm: () => void;
  onDownload: () => void;
}) {
  const actions = noticeActions(change);
  const closed = !canCancel(change);   // applied or cancelled: C2 moves it no further, so a step not recorded never will be
  const price = money(change.new_price, change.currency);
  const steps: Array<NoticeStep | "notice_sent"> = [...NOTICE_STEPS, "notice_sent"];
  return (
    <Card data-notice-card>
      <CardContent className="flex flex-col gap-3 py-4">
        <div>
          <p className="text-sm font-semibold text-ks-ink">{N.title}</p>
          <p className="text-xs text-ks-grey-1">{N.intro(change.notice_days)}</p>
        </div>
        <ol className="flex flex-col gap-2">
          {steps.map((kind) => {
            const last = lastStep(events, kind);
            return (
              <li key={kind} className="flex flex-wrap items-baseline justify-between gap-2 text-sm" data-notice-step={kind} data-done={last ? "true" : "false"}>
                <span className={last ? "text-ks-ink" : "text-ks-grey-1"}>{N.step[kind]}</span>
                <span className="text-xs text-ks-grey-1">{last ? N.by(personName(last.actor), stamp(last.at)) : closed ? N.notRecorded : N.todo}</span>
                {last?.note && <span className="basis-full text-xs text-ks-grey-1">{last.note}</span>}
              </li>
            );
          })}
        </ol>
        {change.state === "notice_sent" && <p className="text-sm text-ks-ink" data-notice-effective>{whenText(change)}</p>}
        {change.state === "planned" && actions.length > 0 && <p className="text-xs text-ks-grey-1">{N.waits(price)}</p>}
        {(actions.length > 0 || canExport(change)) && (
          <div className="flex flex-wrap gap-1">
            {actions.filter((a): a is NoticeStep => a !== "notice_sent").map((kind) => (
              <Button key={kind} size="sm" variant="outline" disabled={busy} onClick={() => onStep(kind)} data-notice-action={kind}>{kind === "store_live" ? N.markLive(price) : N.mark[kind]}</Button>
            ))}
            {actions.includes("notice_sent") && <Button size="sm" disabled={busy} onClick={onConfirm} data-notice-action="notice_sent">{N.confirm}</Button>}
            {canExport(change) && <Button size="sm" variant="outline" disabled={busy} onClick={onDownload} data-notice-download>{downloading ? N.downloading : N.download}</Button>}
          </div>
        )}
        {canExport(change) && <p className="text-xs text-ks-grey-1">{N.downloadHint}</p>}
      </CardContent>
    </Card>
  );
}

/** The Confirm notice sent form: an optional note, Not yet and Confirm notice sent. Its own component so the server-side harness
 * proves it outside the dialog, as the cancel form is. */
export function NoticeSentForm({ pending, onKeep, onConfirm }: { pending: boolean; onKeep: () => void; onConfirm: (note: string) => void }) {
  const [note, setNote] = useState("");
  return (
    <div className="flex flex-col gap-2" data-notice-form>
      <Label htmlFor="pp-notice-note" className="text-sm">{N.dialog.label}</Label>
      <Textarea id="pp-notice-note" value={note} maxLength={MAX_REASON} rows={2} placeholder={N.dialog.placeholder} onChange={(e) => setNote(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Escape") onKeep(); }} />
      <p className="text-xs text-ks-grey-1">{N.dialog.hint}</p>
      <div className="flex justify-end gap-1">
        <Button size="sm" variant="outline" data-nonmutating disabled={pending} onClick={onKeep}>{N.dialog.keep}</Button>
        <Button size="sm" disabled={pending} onClick={() => onConfirm(note)}>{pending ? N.dialog.pending : N.confirm}</Button>
      </div>
    </div>
  );
}

/** The retry form (C1 v1.47): Not now and Retry. Its own component so the server-side harness proves it outside the dialog, as the
 * cancel form is. Retry is a write: not marked non-mutating, so a person with the view alone never sees it. */
export function RetryForm({ pending, onKeep, onConfirm }: { pending: boolean; onKeep: () => void; onConfirm: () => void }) {
  return (
    <div className="flex justify-end gap-1" data-retry-form>
      <Button size="sm" variant="outline" data-nonmutating disabled={pending} onClick={onKeep}>{RT.keep}</Button>
      <Button size="sm" disabled={pending} onClick={onConfirm} data-retry-confirm>{pending ? RT.pending : RT.confirm}</Button>
    </div>
  );
}

/** The status or alert of a cancel. While the dialog is open it reads inside the dialog, where the person is (a refusal under the
 * overlay would never be seen; round 1, R1-1); once closed, on the page above the header. */
export function NoteLine({ note }: { note: Note }) {
  return <p role={note.tone === "err" ? "alert" : "status"} className={`text-sm ${note.tone === "err" ? "text-ks-danger" : "text-ks-grey-1"}`} data-change-note>{note.text}</p>;
}

/** The cancel form: a required reason (the screen refuses an empty one before C2 does), Keep change and Cancel change. The
 * form is its own component so the server-side harness can render it outside the dialog. */
export function CancelChangeForm({ pending, onKeep, onCancel, onNote }: {
  pending: boolean; onKeep: () => void; onCancel: (reason: string) => void; onNote: (note: Note | null) => void;
}) {
  const [reason, setReason] = useState("");
  const submit = () => {
    if (!reason.trim()) { onNote({ tone: "err", text: K.empty }); return; }
    onNote(null); onCancel(reason);
  };
  return (
    <div className="flex flex-col gap-2" data-change-cancel-form>
      <Label htmlFor="pp-change-cancel-reason" className="text-sm">{K.label}</Label>
      <Textarea id="pp-change-cancel-reason" value={reason} maxLength={MAX_REASON} rows={2} placeholder={K.placeholder} onChange={(e) => setReason(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Escape") onKeep(); }} />
      <p className="text-xs text-ks-grey-1">{K.hint}</p>
      <div className="flex justify-end gap-1">
        <Button size="sm" variant="outline" data-nonmutating disabled={pending} onClick={onKeep}>{K.keep}</Button>
        <Button size="sm" variant="destructive" disabled={pending} onClick={submit}>{pending ? K.pending : K.confirm}</Button>
      </div>
    </div>
  );
}

function ProductCell({ row, region, onOpen }: { row: ChangeRow; region: string; onOpen: () => void }) {
  return (
    <div>
      <Button variant="link" size="sm" className="h-auto p-0 font-semibold text-ks-ink" data-nonmutating onClick={(e) => { e.stopPropagation(); onOpen(); }} aria-label={`${C.open}: ${productName(row)}`}>{productName(row)}</Button>
      <div className="text-xs text-ks-grey-1">{row.xero_sku} · {region}</div>
    </div>
  );
}
function ChangeCell({ row }: { row: ChangeRow }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Badge variant="outline" className={CHIP_TONE[row.direction]}>{C.chip[row.direction]}</Badge>
      <span className="tabular-nums text-ks-ink">{priceText(row)}</span>
      {row.source === "drift_correction" && <span className="text-xs text-ks-grey-1" data-change-correction>{C.correction}</span>}
    </div>
  );
}
function StateChip({ state }: { state: ChangeRow["state"] }) {
  return <Badge variant="outline" className={STATE_TONE[state]} data-change-state={state}>{C.state[state]}</Badge>;
}
/** The State column: the chip, and on a change the applier has run, how many subscriptions failed (C1 v1.47). */
function StateCell({ row }: { row: ChangeRow }) {
  const ran = row.state === "applying" || row.state === "applied";
  return (
    <div className="flex flex-wrap items-center gap-1">
      <StateChip state={row.state} />
      {ran && row.targets.failed > 0 && <Badge variant="outline" className="border-ks-danger text-ks-danger" data-change-failed={row.targets.failed}>{C.failedCount(row.targets.failed)}</Badge>}
    </div>
  );
}
function TargetCell({ target }: { target: ChangeTarget }) {
  const name = [target.product_title, target.variant_title].filter(Boolean).join(" ");
  const facts = [name || null, target.quantity > 1 ? T.drift.quantity(target.quantity) : null].filter(Boolean).join(" · ");
  return <div><span className="tabular-nums text-sm text-ks-ink">{T.drift.subscription(target.subscription_id)}</span>{facts && <div className="text-xs text-ks-grey-1">{facts}</div>}</div>;
}
/** A target's result (C1 v1.47): the state, C2's result in words, the exception kept on it, and Retry on a failed one the person may
 * retry (a write, offered to the module edit alone). */
function ResultCell({ target, currency, waiting, allowed, busy, onRetry }: {
  target: ChangeTarget; currency: string; waiting: boolean; allowed: boolean; busy: boolean; onRetry: () => void;
}) {
  const why = resultText(target, currency, waiting);
  const held = target.exception ?? null;
  return (
    <div>
      <div className="flex flex-wrap items-center gap-1">
        <Badge variant="outline" className={RESULT_TONE[target.state]} data-target-state={target.state}>{D.result[target.state]}</Badge>
        {allowed && <Button size="sm" variant="outline" className="h-6 px-2 text-xs" disabled={busy} onClick={onRetry} data-target-retry={target.subscription_id}>{RT.button}</Button>}
      </div>
      {why && <div className="text-xs text-ks-grey-1" data-target-result={target.subscription_id}>{why}</div>}
      {held && (
        <div className="text-xs text-ks-grey-1" data-target-exception={target.subscription_id}>
          {held.spent_at ? X.spent(humanDate(held.spent_at), held.reason) : X.left(held.deliveries_remaining, held.reason)}
          <div>{X.by(personName(held.logged_by), stamp(held.logged_at))}</div>
        </div>
      )}
    </div>
  );
}
